import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alertActions } from "../redux/AlertController";
import useLogout from "./useLogout";

const DEFAULT_FILTERS = { city: '', referenceNumber: 'APP-', phoneNumber: '', status: 'pending', group: '' };
const PROGRESS_POLL_MS = 1500;

export const useApplications = () => {
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [isAcceptingAll, setIsAcceptingAll] = useState(false);

    const [unverifiedApplications, setUnverifiedApplications] = useState([]);
    const [unverifiedCurrentPage, setUnverifiedCurrentPage] = useState(1);
    const [unverifiedTotalPages, setUnverifiedTotalPages] = useState(1);
    const [unverifiedTotal, setUnverifiedTotal] = useState(0);
    const [isLoadingUnverified, setIsLoadingUnverified] = useState(false);
    const [isDeletingUnverified, setIsDeletingUnverified] = useState(false);
    const [isSendingReminders, setIsSendingReminders] = useState(false);
    const [reminderProgress, setReminderProgress] = useState(null);

    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { logout } = useLogout();
    const reminderPollTimeoutRef = useRef(null);

    const fetchApplications = async (page = 1, activeFilters = filters) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            Object.entries(activeFilters).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications?${params}`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setApplications(json.applications);
                setCurrentPage(json.currentPage);
                setTotalPages(json.totalPages);
                setTotal(json.total);
                setGrandTotal(json.grandTotal);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoading(false);
    };

    const applyFilters = (newFilters) => {
        setFilters(newFilters);
        fetchApplications(1, newFilters);
    };

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        fetchApplications(1, DEFAULT_FILTERS);
    };

    const updateApplicationStatus = async (id, { status, rejectionReason }) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ status, rejectionReason })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
                return null;
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
                return null;
            }

            dispatch(alertActions.showAlert({ msg: 'تم تحديث حالة الطلب بنجاح', type: 'success' }));
            fetchApplications(currentPage, filters);
            return json;
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
            return null;
        }
    };

    // Accepts only the ids passed in (whatever is currently on-screen) — never
    // a blanket "accept every pending application in the database", so an
    // application submitted after this page loaded is never swept in.
    const acceptAllVisible = async () => {
        const pendingIds = applications.filter((a) => a.status === 'pending').map((a) => a._id);
        if (pendingIds.length === 0) {
            dispatch(alertActions.showAlert({ msg: 'لا يوجد طلبات معلقة في القائمة الحالية', type: 'error' }));
            return;
        }

        setIsAcceptingAll(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/accept-bulk`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ ids: pendingIds })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: `تم قبول ${json.acceptedCount} طلب بنجاح`, type: 'success' }));
                fetchApplications(currentPage, filters);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsAcceptingAll(false);
    };

    const fetchUnverified = async (page = 1) => {
        setIsLoadingUnverified(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/unverified?page=${page}&limit=150`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setUnverifiedApplications(json.applications);
                setUnverifiedCurrentPage(json.currentPage);
                setUnverifiedTotalPages(json.totalPages);
                setUnverifiedTotal(json.total);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoadingUnverified(false);
    };

    const deleteUnverified = async (ids) => {
        setIsDeletingUnverified(true);
        let success = false;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/unverified`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ ids })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: `تم حذف ${json.deletedCount} طلب بنجاح`, type: 'success' }));
                success = true;
                fetchUnverified(unverifiedCurrentPage);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsDeletingUnverified(false);
        return success;
    };

    // Polls the reminder-send batch's progress until it completes, same
    // pattern as the notifications page's history polling.
    const pollReminderProgress = (historyId) => {
        const poll = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/unverified/reminder-progress/${historyId}`, {
                    headers: { 'Authorization': `Bearer ${user?.token}` }
                });
                const json = await res.json();
                if (!res.ok) return;

                setReminderProgress(json.entry.sendProgress);

                if (!json.entry.sendProgress.isComplete) {
                    reminderPollTimeoutRef.current = setTimeout(poll, PROGRESS_POLL_MS);
                }
            } catch (err) {
                // Silent — polling just stops, the admin already saw the
                // "sending to N" toast that confirmed the action started.
            }
        };
        poll();
    };

    // Sends only the ids passed in (whatever is currently on-screen in the
    // unverified list) their pending OTP as a reminder, with live progress.
    const sendUnverifiedReminders = async (ids) => {
        setIsSendingReminders(true);
        setReminderProgress(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/unverified/send-reminders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ ids })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'success' }));
                setReminderProgress({ sent: 0, total: json.count, isComplete: false });
                pollReminderProgress(json.historyId);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsSendingReminders(false);
    };

    useEffect(() => {
        return () => clearTimeout(reminderPollTimeoutRef.current);
    }, []);

    useEffect(() => {
        if (!user) {
            navigate('/login');
        } else {
            fetchApplications(1, DEFAULT_FILTERS);
        }
    }, [user]);

    return {
        applications,
        isLoading,
        currentPage,
        totalPages,
        total,
        grandTotal,
        filters,
        fetchApplications,
        applyFilters,
        clearFilters,
        updateApplicationStatus,
        acceptAllVisible,
        isAcceptingAll,
        unverifiedApplications,
        unverifiedCurrentPage,
        unverifiedTotalPages,
        unverifiedTotal,
        isLoadingUnverified,
        fetchUnverified,
        deleteUnverified,
        isDeletingUnverified,
        sendUnverifiedReminders,
        isSendingReminders,
        reminderProgress
    };
};
